import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import { Camera, CheckCircle2, Clock3, MapPin, PhoneCall, XCircle } from 'lucide-react';
import api from '../lib/api';
import 'leaflet/dist/leaflet.css';

type CaptureStatus =
  | 'PENDING_REVIEW'
  | 'NOVO'
  | 'EM_ANALISE'
  | 'APROVADO'
  | 'REJEITADO'
  | 'PUBLICADO'
  | 'AJUSTE_SOLICITADO'
  | 'NEGOCIO_FECHADO';

interface UserOption {
  id: string;
  name: string;
  email: string;
  profile?: string;
}

interface Prospector {
  id: string;
  name: string;
  phone: string;
  email?: string;
  type: 'INTERNO' | 'EXTERNO';
}

interface CapturedMedia {
  id?: string;
  type: 'FOTO' | 'VIDEO' | 'PDF';
  url: string;
  filename: string;
  size?: number;
  order?: number;
  isPrimary?: boolean;
}

interface CapturedProperty {
  id: string;
  ownerName?: string;
  title?: string;
  city: string;
  state: string;
  region?: string;
  street: string;
  number: string;
  neighborhood: string;
  latitude?: number | string;
  longitude?: number | string;
  estimatedValue: number | string;
  status: CaptureStatus;
  statusColor?: string;
  description: string;
  media?: CapturedMedia[];
  responsibleUser?: { name: string };
  prospector?: { name: string; phone?: string };
  createdAt?: string;
}

const DEFAULT_MAP_CENTER: [number, number] = [-25.4284, -49.2733];

const toNumber = (value?: string | number) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const markerFromColor = (color: string) =>
  L.divIcon({
    className: '',
    html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 0 2px rgba(0,0,0,.2)"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const formatCurrency = (value?: string | number) => {
  const numeric = toNumber(value);
  if (numeric === undefined) return '-';
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const statusLabels: Record<CaptureStatus, string> = {
  PENDING_REVIEW: 'Pendente de revisao',
  NOVO: 'Novo',
  EM_ANALISE: 'Em analise',
  APROVADO: 'Aprovado',
  REJEITADO: 'Rejeitado',
  PUBLICADO: 'Publicado',
  AJUSTE_SOLICITADO: 'Ajuste solicitado',
  NEGOCIO_FECHADO: 'Negocio fechado',
};

export default function PropertyCapturePage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [properties, setProperties] = useState<CapturedProperty[]>([]);
  const [pendingQueue, setPendingQueue] = useState<CapturedProperty[]>([]);
  const [mapProperties, setMapProperties] = useState<CapturedProperty[]>([]);
  const [prospectors, setProspectors] = useState<Prospector[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [tab, setTab] = useState<'capture' | 'queue' | 'map'>('capture');
  const [queueFilter, setQueueFilter] = useState<'ALL' | CaptureStatus>('ALL');

  const [prospectorForm, setProspectorForm] = useState({
    name: '',
    phone: '',
    email: '',
    type: 'INTERNO',
    commissionDefault: '',
  });

  const [propertyForm, setPropertyForm] = useState({
    type: 'CASA',
    businessType: 'VENDA',
    responsibleUserId: '',
    prospectorId: '',
    ownerName: '',
    title: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    region: '',
    latitude: '',
    longitude: '',
    estimatedValue: '',
    description: '',
    videoUrl: '',
    commissionPercentage: '',
  });

  const [mediaItems, setMediaItems] = useState<CapturedMedia[]>([]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prospectorsRes, propertiesRes, queueRes, mapRes, usersRes] = await Promise.all([
        api.get('/property-capture/prospectors'),
        api.get('/property-capture/properties?limit=100'),
        api.get('/property-capture/properties/pending'),
        api.get('/property-capture/properties/map'),
        api.get('/users?limit=200'),
      ]);

      setProspectors(prospectorsRes.data || []);
      setProperties(propertiesRes.data?.data || []);
      setPendingQueue(queueRes.data || []);
      setMapProperties(mapRes.data || []);
      setUsers(usersRes.data?.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados de angariacao:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!propertyForm.responsibleUserId && users.length > 0) {
      setPropertyForm((prev) => ({
        ...prev,
        responsibleUserId: users[0].id,
      }));
    }
  }, [users, propertyForm.responsibleUserId]);

  const mapCenter = useMemo<[number, number]>(() => {
    const first = mapProperties.find(
      (item) => toNumber(item.latitude) !== undefined && toNumber(item.longitude) !== undefined,
    );

    if (!first) return DEFAULT_MAP_CENTER;

    return [toNumber(first.latitude)!, toNumber(first.longitude)!];
  }, [mapProperties]);

  const filteredQueue = useMemo(() => {
    if (queueFilter === 'ALL') return pendingQueue;
    return pendingQueue.filter((item) => item.status === queueFilter);
  }, [pendingQueue, queueFilter]);

  const resetPropertyForm = () => {
    setPropertyForm((prev) => ({
      ...prev,
      ownerName: '',
      title: '',
      zipCode: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      region: '',
      latitude: '',
      longitude: '',
      estimatedValue: '',
      description: '',
      videoUrl: '',
      commissionPercentage: '',
      prospectorId: '',
    }));
    setMediaItems([]);
  };

  const handleCreateProspector = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await api.post('/property-capture/prospectors', {
        name: prospectorForm.name,
        phone: prospectorForm.phone,
        email: prospectorForm.email || undefined,
        type: prospectorForm.type,
        commissionDefault: prospectorForm.commissionDefault
          ? Number(prospectorForm.commissionDefault)
          : undefined,
      });
      setProspectorForm({
        name: '',
        phone: '',
        email: '',
        type: 'INTERNO',
        commissionDefault: '',
      });
      await loadData();
    } catch (error) {
      console.error('Erro ao cadastrar captador:', error);
      alert('Nao foi possivel cadastrar o captador.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateProperty = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!propertyForm.responsibleUserId) {
      alert('Selecione o usuario responsavel.');
      return;
    }

    if (mediaItems.filter((item) => item.type === 'FOTO').length < 1) {
      alert('Adicione ao menos 1 foto para o cadastro de angariacao.');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/property-capture/properties', {
        ...propertyForm,
        prospectorId: propertyForm.prospectorId || undefined,
        latitude: propertyForm.latitude ? Number(propertyForm.latitude) : undefined,
        longitude: propertyForm.longitude ? Number(propertyForm.longitude) : undefined,
        estimatedValue: Number(propertyForm.estimatedValue),
        commissionPercentage: propertyForm.commissionPercentage
          ? Number(propertyForm.commissionPercentage)
          : undefined,
        media: mediaItems,
      });

      resetPropertyForm();
      setTab('queue');
      await loadData();
    } catch (error) {
      console.error('Erro ao cadastrar imovel:', error);
      alert('Nao foi possivel cadastrar o imovel captado.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (
    propertyId: string,
    status: CaptureStatus,
    notes?: string,
    adjustmentRequest?: string,
  ) => {
    try {
      await api.patch(`/property-capture/properties/${propertyId}/status`, {
        status,
        notes,
        adjustmentRequest,
      });
      await loadData();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Nao foi possivel atualizar o status deste imovel.');
    }
  };

  const captureCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocalizacao nao suportada neste dispositivo.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPropertyForm((prev) => ({
          ...prev,
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        }));
      },
      () => {
        alert('Nao foi possivel obter a localizacao atual.');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const fileToDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleMobileUpload = async (files: FileList | null) => {
    if (!files?.length) return;

    const converted = await Promise.all(
      Array.from(files).map(async (file, index) => ({
        type: 'FOTO' as const,
        url: await fileToDataUrl(file),
        filename: file.name,
        size: file.size,
        order: mediaItems.length + index,
        isPrimary: mediaItems.length + index === 0,
      })),
    );

    setMediaItems((prev) => [...prev, ...converted]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando modulo de angariacao...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Modulo de Angariacao de Imoveis</h1>
          <p className="text-gray-600">Cadastro, validacao, fila de analise e mapa interativo de captacao.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={`btn ${tab === 'capture' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('capture')}>
            Cadastro
          </button>
          <button className={`btn ${tab === 'queue' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('queue')}>
            Fila de analise
          </button>
          <button className={`btn ${tab === 'map' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('map')}>
            Mapa
          </button>
        </div>
      </div>

      {tab === 'capture' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="card space-y-4">
            <h2 className="text-lg font-semibold">Novo Captador</h2>
            <form className="space-y-3" onSubmit={handleCreateProspector}>
              <div>
                <label className="label">Nome</label>
                <input className="input" value={prospectorForm.name} onChange={(e) => setProspectorForm((p) => ({ ...p, name: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Telefone</label>
                <input className="input" value={prospectorForm.phone} onChange={(e) => setProspectorForm((p) => ({ ...p, phone: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Email</label>
                <input className="input" type="email" value={prospectorForm.email} onChange={(e) => setProspectorForm((p) => ({ ...p, email: e.target.value }))} />
              </div>
              <div>
                <label className="label">Tipo</label>
                <select className="input" value={prospectorForm.type} onChange={(e) => setProspectorForm((p) => ({ ...p, type: e.target.value }))}>
                  <option value="INTERNO">Corretor interno</option>
                  <option value="EXTERNO">Parceiro externo</option>
                </select>
              </div>
              <div>
                <label className="label">Comissao padrao (%)</label>
                <input className="input" type="number" min={0} max={100} step="0.01" value={prospectorForm.commissionDefault} onChange={(e) => setProspectorForm((p) => ({ ...p, commissionDefault: e.target.value }))} />
              </div>
              <button className="btn-primary w-full" disabled={submitting}>Cadastrar captador</button>
            </form>

            <div className="pt-4 border-t">
              <h3 className="font-medium mb-2">Captadores cadastrados</h3>
              <div className="max-h-60 overflow-auto space-y-2">
                {prospectors.map((item) => (
                  <div key={item.id} className="p-3 rounded-lg border bg-gray-50 text-sm">
                    <p className="font-medium">{item.name}</p>
                    <p className="text-gray-600">{item.type} • {item.phone}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Cadastro de Imovel Captado</h2>
              <button className="btn-secondary flex items-center gap-2" type="button" onClick={captureCurrentLocation}>
                <MapPin className="w-4 h-4" />
                Localizacao automatica
              </button>
            </div>

            <form className="grid grid-cols-1 gap-3 md:grid-cols-2" onSubmit={handleCreateProperty}>
              <div>
                <label className="label">Tipo</label>
                <select className="input" value={propertyForm.type} onChange={(e) => setPropertyForm((p) => ({ ...p, type: e.target.value }))}>
                  <option value="CASA">Casa</option>
                  <option value="APARTAMENTO">Apartamento</option>
                  <option value="TERRENO">Terreno</option>
                  <option value="COMERCIAL">Comercial</option>
                  <option value="OUTRO">Outro</option>
                </select>
              </div>
              <div>
                <label className="label">Negocio</label>
                <select className="input" value={propertyForm.businessType} onChange={(e) => setPropertyForm((p) => ({ ...p, businessType: e.target.value }))}>
                  <option value="VENDA">Venda</option>
                  <option value="LOCACAO">Locacao</option>
                </select>
              </div>
              <div>
                <label className="label">Responsavel</label>
                <select className="input" value={propertyForm.responsibleUserId} onChange={(e) => setPropertyForm((p) => ({ ...p, responsibleUserId: e.target.value }))} required>
                  <option value="">Selecione...</option>
                  {users.map((item) => (
                    <option key={item.id} value={item.id}>{item.name} ({item.profile || 'SEM PERFIL'})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Captador</label>
                <select className="input" value={propertyForm.prospectorId} onChange={(e) => setPropertyForm((p) => ({ ...p, prospectorId: e.target.value }))}>
                  <option value="">Nao vincular</option>
                  {prospectors.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label">Nome do proprietario</label>
                <input className="input" value={propertyForm.ownerName} onChange={(e) => setPropertyForm((p) => ({ ...p, ownerName: e.target.value }))} />
              </div>
              <div>
                <label className="label">CEP</label>
                <input className="input" value={propertyForm.zipCode} onChange={(e) => setPropertyForm((p) => ({ ...p, zipCode: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Rua</label>
                <input className="input" value={propertyForm.street} onChange={(e) => setPropertyForm((p) => ({ ...p, street: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Numero</label>
                <input className="input" value={propertyForm.number} onChange={(e) => setPropertyForm((p) => ({ ...p, number: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Complemento</label>
                <input className="input" value={propertyForm.complement} onChange={(e) => setPropertyForm((p) => ({ ...p, complement: e.target.value }))} />
              </div>
              <div>
                <label className="label">Bairro</label>
                <input className="input" value={propertyForm.neighborhood} onChange={(e) => setPropertyForm((p) => ({ ...p, neighborhood: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Cidade</label>
                <input className="input" value={propertyForm.city} onChange={(e) => setPropertyForm((p) => ({ ...p, city: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Estado</label>
                <input className="input" value={propertyForm.state} onChange={(e) => setPropertyForm((p) => ({ ...p, state: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Regiao</label>
                <input className="input" value={propertyForm.region} onChange={(e) => setPropertyForm((p) => ({ ...p, region: e.target.value }))} />
              </div>
              <div>
                <label className="label">Latitude</label>
                <input className="input" value={propertyForm.latitude} onChange={(e) => setPropertyForm((p) => ({ ...p, latitude: e.target.value }))} />
              </div>
              <div>
                <label className="label">Longitude</label>
                <input className="input" value={propertyForm.longitude} onChange={(e) => setPropertyForm((p) => ({ ...p, longitude: e.target.value }))} />
              </div>
              <div>
                <label className="label">Valor estimado</label>
                <input className="input" type="number" min={0} value={propertyForm.estimatedValue} onChange={(e) => setPropertyForm((p) => ({ ...p, estimatedValue: e.target.value }))} required />
              </div>
              <div>
                <label className="label">Comissao (%)</label>
                <input className="input" type="number" min={0} max={100} step="0.01" value={propertyForm.commissionPercentage} onChange={(e) => setPropertyForm((p) => ({ ...p, commissionPercentage: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Descricao detalhada</label>
                <textarea className="input min-h-[90px]" value={propertyForm.description} onChange={(e) => setPropertyForm((p) => ({ ...p, description: e.target.value }))} required />
              </div>
              <div className="md:col-span-2">
                <label className="label">Video (URL opcional)</label>
                <input className="input" value={propertyForm.videoUrl} onChange={(e) => setPropertyForm((p) => ({ ...p, videoUrl: e.target.value }))} />
              </div>

              <div className="md:col-span-2 rounded-lg border border-dashed p-4 bg-gray-50">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">Upload mobile (foto + localizacao)</p>
                    <p className="text-xs text-gray-600">Use o celular para capturar foto diretamente da camera.</p>
                  </div>
                  <label className="btn-secondary cursor-pointer flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Capturar fotos
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={(e) => handleMobileUpload(e.target.files)}
                    />
                  </label>
                </div>

                {!!mediaItems.length && (
                  <div className="mt-3 space-y-2 max-h-40 overflow-auto">
                    {mediaItems.map((item, index) => (
                      <div key={`${item.filename}-${index}`} className="text-xs flex items-center justify-between p-2 bg-white rounded border">
                        <span>{item.filename}</span>
                        <button
                          type="button"
                          className="text-red-600"
                          onClick={() => setMediaItems((prev) => prev.filter((_, i) => i !== index))}
                        >
                          remover
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <button className="btn-primary w-full" disabled={submitting}>
                  Salvar imovel de angariacao
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {tab === 'queue' && (
        <div className="space-y-4">
          <div className="card flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Fila de Analise</h2>
              <p className="text-sm text-gray-600">Aprovacao por gerente/diretor/admin com acoes rapidas.</p>
            </div>
            <select className="input max-w-xs" value={queueFilter} onChange={(e) => setQueueFilter(e.target.value as any)}>
              <option value="ALL">Todos pendentes</option>
              <option value="PENDING_REVIEW">Pendente de revisao</option>
              <option value="NOVO">Novo</option>
              <option value="EM_ANALISE">Em analise</option>
              <option value="AJUSTE_SOLICITADO">Ajuste solicitado</option>
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {filteredQueue.map((item) => (
              <div key={item.id} className="card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{item.ownerName || item.title || 'Imovel captado'}</h3>
                    <p className="text-sm text-gray-600">{item.street}, {item.number} - {item.neighborhood} - {item.city}/{item.state}</p>
                    <p className="text-sm mt-1"><strong>Valor:</strong> {formatCurrency(item.estimatedValue)}</p>
                    <p className="text-xs text-gray-500 mt-1">Status atual: {statusLabels[item.status]}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button className="btn-secondary flex items-center gap-1" onClick={() => updateStatus(item.id, 'EM_ANALISE', 'Em analise pela gerencia')}>
                      <Clock3 className="w-4 h-4" />
                      Em analise
                    </button>
                    <button className="btn-primary flex items-center gap-1" onClick={() => updateStatus(item.id, 'APROVADO', 'Imovel aprovado para operacao')}>
                      <CheckCircle2 className="w-4 h-4" />
                      Aprovar
                    </button>
                    <button className="btn-danger flex items-center gap-1" onClick={() => updateStatus(item.id, 'REJEITADO', 'Nao aprovado pela gerencia')}>
                      <XCircle className="w-4 h-4" />
                      Rejeitar
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        const reason = prompt('Informe o ajuste solicitado:');
                        if (!reason) return;
                        updateStatus(item.id, 'AJUSTE_SOLICITADO', 'Ajuste solicitado', reason);
                      }}
                    >
                      Solicitar ajuste
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {!filteredQueue.length && (
              <div className="card text-sm text-gray-500">Nenhum imovel pendente na fila de analise.</div>
            )}
          </div>
        </div>
      )}

      {tab === 'map' && (
        <div className="space-y-4">
          <div className="card">
            <h2 className="text-lg font-semibold">Mapa Interativo de Captacao</h2>
            <p className="text-sm text-gray-600">Filtre por regiao no backend e acompanhe os imoveis em tempo real.</p>
          </div>

          <div className="card p-0 overflow-hidden">
            <MapContainer center={mapCenter} zoom={12} className="h-[600px] w-full" scrollWheelZoom>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {mapProperties
                .filter((item) => toNumber(item.latitude) !== undefined && toNumber(item.longitude) !== undefined)
                .map((item) => {
                  const lat = toNumber(item.latitude)!;
                  const lng = toNumber(item.longitude)!;
                  const color = item.statusColor || '#64748b';

                  return (
                    <Marker key={item.id} position={[lat, lng]} icon={markerFromColor(color)}>
                      <Popup>
                        <div className="space-y-1 text-sm">
                          <p className="font-semibold">{item.ownerName || item.title || 'Imovel captado'}</p>
                          <p>{item.city}/{item.state}</p>
                          <p>{item.street}, {item.number}</p>
                          <p><strong>Status:</strong> {statusLabels[item.status]}</p>
                          <p><strong>Valor:</strong> {formatCurrency(item.estimatedValue)}</p>
                          {item.prospector?.name && (
                            <p className="flex items-center gap-1">
                              <PhoneCall className="w-3 h-3" />
                              Captador: {item.prospector.name}
                            </p>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
            </MapContainer>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="font-semibold mb-2">Resumo rapido</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="rounded-lg border p-3">
            <p className="text-gray-600">Total captados</p>
            <p className="text-xl font-semibold">{properties.length}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-gray-600">Pendentes</p>
            <p className="text-xl font-semibold">{pendingQueue.length}</p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-gray-600">Com localizacao</p>
            <p className="text-xl font-semibold">
              {mapProperties.filter((item) => toNumber(item.latitude) !== undefined && toNumber(item.longitude) !== undefined).length}
            </p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-gray-600">Captadores</p>
            <p className="text-xl font-semibold">{prospectors.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
